import React, { useMemo, useState, useEffect } from 'react';
import EmptyState from './EmptyState';
import editIcon from './../../assets/edit-icon.png';
import trashIcon from './../../assets/trash-icon.png';
import saveIcon from './../../assets/blue-save-icon.png';
import resolveIcon from './../../assets/resolve-icon.png';
import greenDot from './../../assets/green-dot.png';
import greyDot from './../../assets/grey-dot.png';
import importDataIcon from './../../assets/import-data-icon.png';
import plusIcon from './../../assets/plus-icon.png';
import paginationLeftIcon from './../../assets/pagination-arrow-left.png';
import paginationRightIcon from './../../assets/pagination-arrow-right.png';
import warningIconCircle from './../../assets/warning-icon-circle.png';
import blueDot from './../../assets/blue-dot.png';
import notedBadge from './../../assets/noted-badge.png';
import DataSourceDisplay from './DataSourceDisplay';
import HoursWorkedTooltip from './HoursWorkedTooltip';
import Cleave from 'cleave.js/react';
import PriorYearPayrollValueTooltip from './PriorYearPayrollValueTooltip';
import './ResultsTable.css';
import {
  formatCurrency,
  formatDateForHTMLInput,
  formatDateForInput,
  getFieldValue,
  getCellColorAndMessage,
  TABLE_HEADERS,
  FIELDS_TO_NORMALIZE,
  MAX_VISIBLE_PAGES
} from './ResultsTableUtility';

const ResultsTable = ({
  data,
  currentPage,
  rowsPerPage,
  isAddingNewRow,
  editableRowIndex,
  editableRowData,
  handlePageChange,
  handleEditRow,
  handleSaveRow,
  handleInputChange,
  handleResolveClick,
  handleBulkResolveClick,
  handleZeroHourClick,
  setIsAddingNewRow,
  setEditableRowIndex,
  initialEditableRowData,
  handleAddRow,
  setEditableRowData,
  setCurrentView,
  isOldData,
  alertFilters,
  handleDeleteRowClick,
  conflictColumns,
  elevateRequiredFields
}) => {
  const [dataSourceDisplay, setDataSourceDisplay] = useState({ source: '', anchorEl: null });
  const [hoursWorkedTooltip, setHoursWorkedTooltip] = useState({ show: false, position: { x: 0, y: 0 } });
  const [priorYearTooltip, setPriorYearTooltip] = useState({ show: false, position: { x: 0, y: 0 } });
  const [tooltip, setTooltip] = useState({ show: false, source: '', position: null });

  const rows = Object.entries(data.Row || {});

  // Sort employees by last name, then first name
  const sortedRows = useMemo(() => {
    return rows.sort(([, employeeA], [, employeeB]) => {
      const lastNameA = getFieldValue(employeeA.LastName, true).toLowerCase();
      const lastNameB = getFieldValue(employeeB.LastName, true).toLowerCase();
      
      if (lastNameA !== lastNameB) {
        return lastNameA.localeCompare(lastNameB);
      }
      
      const firstNameA = getFieldValue(employeeA.FirstName, true).toLowerCase();
      const firstNameB = getFieldValue(employeeB.FirstName, true).toLowerCase();
      return firstNameA.localeCompare(firstNameB);
    });
  }, [rows]);

  // Force scrollbars to always be visible
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .table-container {
        -webkit-overflow-scrolling: touch;
        overflow-y: hidden;
        overflow-x: scroll;
      }
    `;
    document.head.appendChild(style);

    const tableContainer = document.querySelector('.table-container');
    tableContainer.style.overflowX = 'scroll';
    tableContainer.style.overflowY = 'hidden';

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Close tooltip on window click
  useEffect(() => {
    const handleWindowClick = () => {
      setTooltip({ show: false, source: '', position: null });
    };
  
    window.addEventListener('click', handleWindowClick);
  
    return () => {
      window.removeEventListener('click', handleWindowClick);
    };
  }, []);

  const handleCellMouseEnter = (event, source) => {
    const rect = event.target.getBoundingClientRect();
    setTooltip({
      show: true,
      source,
      position: { x: rect.left, y: rect.bottom }
    });
  };

  const handleCellMouseLeave = () => {
    setTooltip({ show: false, source: '', position: null });
  };

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  const startRow = (currentPage - 1) * rowsPerPage;
  const endRow = startRow + rowsPerPage;
  const currentRows = sortedRows.slice(startRow, endRow);

  const showEmptyState = !isAddingNewRow && (rows.length === 0);

  const renderTableHeader = () => {
    return (
      <thead>
        <tr>
          {TABLE_HEADERS.map(header => (
            <th key={header.field}>
              {header.name}
              {conflictColumns.includes(header.field) && (
                <button className="resolve-button-header" onClick={() => handleBulkResolveClick(header.field)}>
                  RESOLVE ALL <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
                </button>
              )}
            </th>
          ))}
          <th className="sticky-column">
            <div className="shadow-overlay"></div>
          </th>
        </tr>
      </thead>
    );
  };

  const renderInputRow = () => {
    const originalData = data.Row[editableRowData.originalKey];
   
    return (
      <tr 
        className="editable-row" 
        key="editable-row"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSaveRow();
          }
        }}
      >
        <td>
          {originalData?.LastName?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.LastName.Values, editableRowData.originalKey, 'LastName', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="text"
                className={`editable-input ${
                  (originalData?.LastName?.FinalValue?.Status === 'Error' || 
                  originalData?.LastName?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                placeholder="ex: Doe"
                value={editableRowData.LastName || ''}
                onChange={(e) => handleInputChange('LastName', e.target.value)}
              />
              {(originalData?.LastName?.FinalValue?.Status === 'Error' || 
                originalData?.LastName?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.LastName.FinalValue.Message}
                </div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.FirstName?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.FirstName.Values, editableRowData.originalKey, 'FirstName', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="text"
                className={`editable-input ${
                  (originalData?.FirstName?.FinalValue?.Status === 'Error' || 
                  originalData?.FirstName?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                placeholder="ex: John"
                value={editableRowData.FirstName || ''}
                onChange={(e) => handleInputChange('FirstName', e.target.value)}
              />
              {(originalData?.FirstName?.FinalValue?.Status === 'Error' || 
                originalData?.FirstName?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.FirstName.FinalValue.Message}
                </div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.DateOfHire?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.DateOfHire.Values, editableRowData.originalKey, 'DateOfHire', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : originalData?.DateOfHire?.FinalValue?.Value?.toLowerCase() === 'rehire' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.DateOfHire.Values, editableRowData.originalKey, 'rehire', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="date"
                className={`editable-input ${
                  (originalData?.DateOfHire?.FinalValue?.Status === 'Error' || 
                  originalData?.DateOfHire?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                value={editableRowData.DateOfHire ? formatDateForHTMLInput(editableRowData.DateOfHire) : ''}
                onChange={(e) => handleInputChange('DateOfHire', e.target.value)}
              />
              {(originalData?.DateOfHire?.FinalValue?.Status === 'Error' || 
                originalData?.DateOfHire?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.DateOfHire.FinalValue.Message}
                </div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.DateOfBirth?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.DateOfBirth.Values, editableRowData.originalKey, 'DateOfBirth', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="date"
                className={`editable-input ${
                  (originalData?.DateOfBirth?.FinalValue?.Status === 'Error' || 
                  originalData?.DateOfBirth?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                value={editableRowData.DateOfBirth ? formatDateForHTMLInput(editableRowData.DateOfBirth) : ''}
                onChange={(e) => handleInputChange('DateOfBirth', e.target.value)}
              />
              {(originalData?.DateOfBirth?.FinalValue?.Status === 'Error' || 
                originalData?.DateOfBirth?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.DateOfBirth.FinalValue.Message}
                </div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.SSN?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.SSN.Values, editableRowData.originalKey, 'SSN', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className={`input-container ${
              (originalData?.SSN?.FinalValue?.Status === 'Error' || 
               originalData?.SSN?.FinalValue?.Status === 'Invalid') && 
               editableRowData.PriorYearEmployee === "true" ? 'has-both-messages' : ''
            }`}>
              <Cleave
                className={`editable-input ${editableRowData.PriorYearEmployee === "true" ? 'disabled-input' : ''} ${
                  (originalData?.SSN?.FinalValue?.Status === 'Error' || 
                  originalData?.SSN?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                placeholder="XXX-XX-XXXX"
                options={{
                  delimiter: '-',
                  blocks: [3, 2, 4],
                  pattern: /[0-9X]/,
                  uppercase: true
                }}
                value={editableRowData.SSN || ''}
                onChange={(e) => handleInputChange('SSN', e.target.value)}
                readOnly={editableRowData.PriorYearEmployee === "true"}
                onMouseEnter={editableRowData.PriorYearEmployee === "true" ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={editableRowData.PriorYearEmployee === "true" ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.SSN?.FinalValue?.Status === 'Error' || 
                originalData?.SSN?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.SSN.FinalValue.Message}
                </div>
              )}
              {editableRowData.PriorYearEmployee === "true" && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.DateOfTermination?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.DateOfTermination.Values, editableRowData.originalKey, 'DateOfTermination', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="date"
                className={`editable-input ${
                  (originalData?.DateOfTermination?.FinalValue?.Status === 'Error' || 
                  originalData?.DateOfTermination?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                }`}
                value={editableRowData.DateOfTermination ? formatDateForHTMLInput(editableRowData.DateOfTermination) : ''}
                onChange={(e) => handleInputChange('DateOfTermination', e.target.value)}
              />
              {(originalData?.DateOfTermination?.FinalValue?.Status === 'Error' || 
                originalData?.DateOfTermination?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.DateOfTermination.FinalValue.Message}
                </div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.HoursWorked?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.HoursWorked.Values, editableRowData.originalKey, 'HoursWorked', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.HoursWorked?.FinalValue?.Status === 'Error' || 
                  originalData?.HoursWorked?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.HoursWorked?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 10,400"
                value={editableRowData.HoursWorked || ''}
                onChange={(e) => handleInputChange('HoursWorked', e.target.value)}
                readOnly={originalData?.HoursWorked?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.HoursWorked?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.HoursWorked?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.HoursWorked?.FinalValue?.Status === 'Error' || 
                originalData?.HoursWorked?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.HoursWorked.FinalValue.Message}
                </div>
              )}
              {originalData?.HoursWorked?.FinalValue?.Source === 'guidant_estimate' && (
                <div className="guidant-estimate-text" style={{ position: 'absolute', bottom: '5px', left: '0' }}>
                  Estimated by Guidant.
                </div>
              )}
              {originalData?.HoursWorked?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.GrossCompensation?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.GrossCompensation.Values, editableRowData.originalKey, 'GrossCompensation', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.GrossCompensation?.FinalValue?.Status === 'Error' || 
                  originalData?.GrossCompensation?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.GrossCompensation?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 3,000"
                value={editableRowData.GrossCompensation || ''}
                onChange={(e) => handleInputChange('GrossCompensation', e.target.value)}
                readOnly={originalData?.GrossCompensation?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.GrossCompensation?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.GrossCompensation?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.GrossCompensation?.FinalValue?.Status === 'Error' || 
                originalData?.GrossCompensation?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.GrossCompensation.FinalValue.Message}
                </div>
              )}
              {originalData?.GrossCompensation?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.DeferALS?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.DeferALS.Values, editableRowData.originalKey, 'DeferALS', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.DeferALS?.FinalValue?.Status === 'Error' || 
                  originalData?.DeferALS?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.DeferALS?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 1,000"
                value={editableRowData.DeferALS || ''}
                onChange={(e) => handleInputChange('DeferALS', e.target.value)}
                readOnly={originalData?.DeferALS?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.DeferALS?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.DeferALS?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.DeferALS?.FinalValue?.Status === 'Error' || 
                originalData?.DeferALS?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.DeferALS.FinalValue.Message}
                </div>
              )}
              {originalData?.DeferALS?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.ParticipantRoth?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.ParticipantRoth.Values, editableRowData.originalKey, 'ParticipantRoth', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.ParticipantRoth?.FinalValue?.Status === 'Error' || 
                  originalData?.ParticipantRoth?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.ParticipantRoth?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 500"
                value={editableRowData.ParticipantRoth || ''}
                onChange={(e) => handleInputChange('ParticipantRoth', e.target.value)}
                readOnly={originalData?.ParticipantRoth?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.ParticipantRoth?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.ParticipantRoth?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.ParticipantRoth?.FinalValue?.Status === 'Error' || 
                originalData?.ParticipantRoth?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.ParticipantRoth.FinalValue.Message}
                </div>
              )}
              {originalData?.ParticipantRoth?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.CompanyMatch?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.CompanyMatch.Values, editableRowData.originalKey, 'CompanyMatch', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.CompanyMatch?.FinalValue?.Status === 'Error' || 
                  originalData?.CompanyMatch?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.CompanyMatch?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 200"
                value={editableRowData.CompanyMatch || ''}
                onChange={(e) => handleInputChange('CompanyMatch', e.target.value)}
                readOnly={originalData?.CompanyMatch?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.CompanyMatch?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.CompanyMatch?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.CompanyMatch?.FinalValue?.Status === 'Error' || 
                originalData?.CompanyMatch?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.CompanyMatch.FinalValue.Message}
                </div>
              )}
              {originalData?.CompanyMatch?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.CompanyProfitSharing?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.CompanyProfitSharing.Values, editableRowData.originalKey, 'CompanyProfitSharing', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.CompanyProfitSharing?.FinalValue?.Status === 'Error' || 
                  originalData?.CompanyProfitSharing?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.CompanyProfitSharing?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 300"
                value={editableRowData.CompanyProfitSharing || ''}
                onChange={(e) => handleInputChange('CompanyProfitSharing', e.target.value)}
                readOnly={originalData?.CompanyProfitSharing?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.CompanyProfitSharing?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.CompanyProfitSharing?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.CompanyProfitSharing?.FinalValue?.Status === 'Error' || 
                originalData?.CompanyProfitSharing?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.CompanyProfitSharing.FinalValue.Message}
                </div>
              )}
              {originalData?.CompanyProfitSharing?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.RollOver?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.RollOver.Values, editableRowData.originalKey, 'RollOver', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.RollOver?.FinalValue?.Status === 'Error' || 
                  originalData?.RollOver?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.RollOver?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 100"
                value={editableRowData.RollOver || ''}
                onChange={(e) => handleInputChange('RollOver', e.target.value)}
                readOnly={originalData?.RollOver?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.RollOver?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.RollOver?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.RollOver?.FinalValue?.Status === 'Error' || 
                originalData?.RollOver?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.RollOver.FinalValue.Message}
                </div>
              )}
              {originalData?.RollOver?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td>
          {originalData?.AnnualLoanPayment?.FinalValue?.Value === 'conflict' ? (
            <button className="resolve-button" onClick={() => handleResolveClick(originalData.AnnualLoanPayment.Values, editableRowData.originalKey, 'AnnualLoanPayment', isOldData)}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className="input-container">
              <input
                type="number"
                className={`editable-input ${
                  (originalData?.AnnualLoanPayment?.FinalValue?.Status === 'Error' || 
                  originalData?.AnnualLoanPayment?.FinalValue?.Status === 'Invalid') 
                  ? 'error-input' 
                  : ''
                } ${originalData?.AnnualLoanPayment?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 'disabled-input' : ''}`}
                placeholder="ex: 150"
                value={editableRowData.AnnualLoanPayment || ''}
                onChange={(e) => handleInputChange('AnnualLoanPayment', e.target.value)}
                readOnly={originalData?.AnnualLoanPayment?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData}
                onMouseEnter={originalData?.AnnualLoanPayment?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  (e) => handleCellMouseEnter(e, "This field cannot be edited because it pertains to an employee from a prior year. If updates are needed, please contact support.") 
                  : undefined}
                onMouseLeave={originalData?.AnnualLoanPayment?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData ? 
                  handleCellMouseLeave 
                  : undefined}
              />
              {(originalData?.AnnualLoanPayment?.FinalValue?.Status === 'Error' || 
                originalData?.AnnualLoanPayment?.FinalValue?.Status === 'Invalid') && (
                <div className="error-message-input">
                  {originalData.AnnualLoanPayment.FinalValue.Message}
                </div>
              )}
              {originalData?.AnnualLoanPayment?.FinalValue?.Source?.toLowerCase() === 'prioryear' && isOldData && (
                <div className="readonly-message">Prior year, read-only.</div>
              )}
            </div>
          )}
        </td>
        <td className="editable-buttons sticky-column">
          <div className="shadow-overlay"></div>
          <div className="sticky-column-content">
            <div className="buttons-container">
              <button className="save-button-row-edit" onClick={handleSaveRow}>
                <img src={saveIcon} alt="Save" className="small-save-icon" />
                SAVE
              </button>
              <button className="cancel-button" onClick={() => { setIsAddingNewRow(false); setEditableRowIndex(null); setEditableRowData(initialEditableRowData); }}>
                X
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  };
  
  const renderCell = (field, fieldName, handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters) => {
    const value = field?.FinalValue?.Value;
    const status = field?.FinalValue?.Status;
    const message = field?.FinalValue?.Message;
    
    const { colorClass, textColorClass, message: cellMessage } = getCellColorAndMessage(status, alertFilters, message, elevateRequiredFields);
  
    const valueProps = {
      onMouseEnter: (e) => handleCellMouseEnter(e, field?.FinalValue?.Source || 'N/A'),
      onMouseLeave: handleCellMouseLeave,
    };
  
    const handleCellClick = () => {
      if (value !== 'conflict') {
        handleEditRow(employeeKey);
      }
    };

    // Last Name cell
    if (fieldName === 'LastName') {
      return (
        <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
          <div className="lastname-container">
            {value === 'conflict' ? (
              <button className="resolve-button" onClick={(e) => {
                e.stopPropagation();
                handleResolveClick(field.Values, employeeKey, fieldName, isOldData);
              }}>
                RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
              </button>
            ) : (
              <span className="name-text" {...valueProps}>{value || '-'}</span>
            )}
          </div>
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
    
    // First Name cell with owner badge
    if (fieldName === 'FirstName') {
      const isOwner = parseFloat(employee?.OwnershipPercent?.FinalValue?.Value || "0") > 0;
      
      return (
        <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
          <div className="firstname-container">
            {value === 'conflict' ? (
              <button className="resolve-button" onClick={(e) => {
                e.stopPropagation();
                handleResolveClick(field.Values, employeeKey, fieldName, isOldData);
              }}>
                RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
              </button>
            ) : (
              <>
                <span className="name-text" {...valueProps}>{value || '-'}</span>
                {isOwner && (
                  <span className="owner-pill">
                    <img src={blueDot} alt="Owner Blue Dot" className="owner-dot" />
                    <span className="owner-text">Owner</span>
                  </span>
                )}
              </>
            )}
          </div>
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
  
    // Status cell (not used in current table but kept for compatibility)
    if (fieldName === 'Status') {
      const displayStatus = (value === 'Death' || value === 'Retirement' || value === 'Terminated' || value === 'Termination' || value === 'Inactive') ? 'Terminated' : 'Active';
      
      return (
        <td className={`status-cell ${colorClass} clickable-cell`} onClick={handleCellClick}>
          {value === 'conflict' ? (
            <button className="resolve-button" onClick={(e) => {
              e.stopPropagation();
              handleResolveClick(field.Values, employeeKey, 'Status', isOldData);
            }}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
          ) : (
            <div className={`status-pill ${displayStatus === 'Terminated' ? 'terminated' : 'active'}`}>
              <img src={displayStatus === 'Terminated' ? greyDot : greenDot} alt="Status Dot" className="status-dot" />
              <span className="status-text" {...valueProps}>{displayStatus}</span>
            </div>
          )}
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
  
    // Special handling for rehire dates
    if (fieldName === 'DateOfHire') {
      const value = field?.FinalValue?.Value;
      if (value && value.toLowerCase() === 'rehire') {
        return (
          <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
            <button className="resolve-button" onClick={(e) => {
              e.stopPropagation();
              handleResolveClick(field.Values, employeeKey, 'rehire', isOldData);
            }}>
              RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
            </button>
            {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
              <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
            )}
          </td>
        );
      }
    }
  
    // Conflict cells
    if (value === 'conflict') {
      return (
        <td className={colorClass}>
          <button className="resolve-button" onClick={() => handleResolveClick(field.Values, employeeKey, fieldName, isOldData)}>
            RESOLVE <img src={resolveIcon} alt="Resolve Icon" className="resolve-icon" />
          </button>
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
  
    // SSN masking
    if (fieldName === 'SSN') {
      if (!value || value.trim() === '') {
        return (
          <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
            <span {...valueProps}>-</span>
            {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
              <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
            )}
          </td>
        );
      }
      const cleanedSSN = value.replace(/-/g, '');
      const maskedSSN = cleanedSSN.length === 9 ? `XXX-XX-${cleanedSSN.slice(-4)}` : cleanedSSN;
      return (
        <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
          <span {...valueProps}>{maskedSSN}</span>
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
  
    // Hours worked special handling
    if (fieldName === 'HoursWorked') {
      let value = field?.FinalValue?.Value || '-';
      if (isNaN(parseFloat(value))) {
        value = '-';
      }
      const hoursFinalValue = field?.FinalValue?.Value;
      const hoursStatus = field?.FinalValue?.Status;
      const hoursSource = field?.FinalValue?.Source;
      const hoursWorkedNoteStatus = employee?.HoursWorkedNote?.FinalValue?.Status;
      const grossCompensation = employee?.GrossCompensation.FinalValue?.Value;
      const hoursWorkedNoteFinalValue = employee?.HoursWorkedNote?.FinalValue?.Value;
      const ownershipPercent = parseFloat(employee?.OwnershipPercent?.FinalValue?.Value || '0');
      const isOwner = ownershipPercent > 0;
      const isGuidantEstimate = hoursSource === 'guidant_estimate';
      const terminationDate = employee?.DateOfTermination?.FinalValue?.Value;
      
      return (
        <td 
          className={`${colorClass} zero-hour-hover ${hoursWorkedNoteFinalValue && !isGuidantEstimate ? 'has-noted-badge' : ''}`}
          onClick={(e) => {
            if (!e.target.classList.contains('noted-badge-icon')) {
              if (hoursWorkedNoteFinalValue && hoursWorkedNoteFinalValue.trim() !== '' && !isGuidantEstimate) {
                handleEditRow(employeeKey);
              } else {
                // If hours is 0 with termination date and no warnings/errors, edit row
                if ((hoursFinalValue === '0' || hoursFinalValue === '0.00' || hoursFinalValue === 0) && 
                    terminationDate && terminationDate.trim() !== '' && 
                    hoursStatus.toLowerCase() !== 'warning' && hoursStatus.toLowerCase() !== 'error') {
                  handleEditRow(employeeKey);
                } else if (hoursFinalValue === '0' || hoursFinalValue === '0.00' || hoursFinalValue === 0 || hoursStatus.toLowerCase() === 'warning' || hoursWorkedNoteStatus.toLowerCase() === "error") {
                  handleZeroHourClick(employeeKey, isOldData, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner);
                } else {
                  handleEditRow(employeeKey);
                }
              }
            }
          }}
        >
          <div className="hours-worked-container">
            <span {...valueProps}>{value}</span>
    
            {(() => {
              if (status === 'Warning') {
                const showTooltip = parseFloat(value) >= 1 && parseFloat(value) <= 499 && isOwner;
    
                if (showTooltip) {
                  return (
                    <img 
                      src={warningIconCircle} 
                      alt="Warning Icon" 
                      className="hours-info-icon" 
                      onMouseEnter={(e) => {
                        const rect = e.target.getBoundingClientRect();
                        setHoursWorkedTooltip({
                          show: true,
                          position: { x: rect.right + 10, y: rect.top }
                        });
                      }}
                      onMouseLeave={() => setHoursWorkedTooltip({ show: false, position: { x: 0, y: 0 } })}
                    />
                  );
                }
              }
            })()}
          </div>
          
          {isGuidantEstimate && (
            <div className="guidant-estimate-text">Estimated by Guidant.</div>
          )}
          
          {hoursWorkedNoteFinalValue && hoursWorkedNoteFinalValue.trim() !== '' && !isGuidantEstimate && (
            <img 
              src={notedBadge} 
              alt="Noted Badge" 
              className="noted-badge-icon noted-badge-hover" 
              onClick={(e) => {
                e.stopPropagation();
                handleZeroHourClick(employeeKey, isOldData, hoursFinalValue, grossCompensation, hoursWorkedNoteFinalValue, isOwner);
              }}
            />
          )}
          
          {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
            <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
          )}
        </td>
      );
    }
  
    const isValueNumber = value && !isNaN(parseFloat(value)) && isFinite(value);
  
    // Default cell rendering
    return (
          <td className={`${colorClass} clickable-cell`} onClick={handleCellClick}>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
              <span {...valueProps}>
                {value ? (FIELDS_TO_NORMALIZE.includes(fieldName) && isValueNumber ? formatCurrency(value) : value) : '-'}
              </span>
              
              {/* Add warning icon for "Last year had value" warnings */}
              {status === 'Warning' && message === 'Last year had value.' && (
                <img 
                  src={warningIconCircle} 
                  alt="Warning Icon" 
                  className="hours-info-icon" 
                  style={{ marginLeft: '80px', cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    const rect = e.target.getBoundingClientRect();
                    setPriorYearTooltip({
                      show: true,
                      position: { x: rect.right + 10, y: rect.top }
                    });
                  }}
                  onMouseLeave={() => setPriorYearTooltip({ show: false, position: { x: 0, y: 0 } })}
                />
              )}
            </div>
            
            {(status === 'Error' || status === 'Warning' || status === 'Invalid') && cellMessage && (
              <div className={`cell-message ${textColorClass}`}>{cellMessage}</div>
            )}
          </td>
        );
  };
  
  const renderEmployeeRow = ([employeeKey, employee], index) => {
    if (editableRowIndex === employeeKey) {
      return renderInputRow();
    }
  
    const FullName = `${getFieldValue(employee.FirstName, true)} ${getFieldValue(employee.LastName, true)}`;
    const fullNameStatus = employee.FirstName?.FinalValue?.Status || employee.LastName?.FinalValue?.Status;
    
    const { colorClass, textColorClass, message: cellMessage } = getCellColorAndMessage(fullNameStatus, alertFilters, employee.FirstName?.FinalValue?.Message || employee.LastName?.FinalValue?.Message, elevateRequiredFields);
  
    return (
      <tr key={employeeKey}>
        {renderCell(employee.LastName, 'LastName', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.FirstName, 'FirstName', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.DateOfHire, 'DateOfHire', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.DateOfBirth, 'DateOfBirth', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.SSN, 'SSN', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.DateOfTermination, 'DateOfTermination', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.HoursWorked, 'HoursWorked', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.GrossCompensation, 'GrossCompensation', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.DeferALS, 'DeferALS', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.ParticipantRoth, 'ParticipantRoth', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.CompanyMatch, 'CompanyMatch', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.CompanyProfitSharing, 'CompanyProfitSharing', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.RollOver, 'RollOver', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        {renderCell(employee.AnnualLoanPayment, 'AnnualLoanPayment', handleResolveClick, handleZeroHourClick, employeeKey, FullName, isOldData, employee, alertFilters)}
        <td className="icons sticky-column">
          <div className="shadow-overlay"></div>
          <div className="sticky-column-content">
            <img src={trashIcon} alt="Delete" className="icon delete-icon" onClick={() => handleDeleteRowClick(employeeKey, isOldData)} />
            <img src={editIcon} alt="Edit" className="icon edit-icon" onClick={() => handleEditRow(employeeKey)} />
          </div>
        </td>
      </tr>
    );
  };
  
  const renderPageNumbers = () => {
    const pageNumbers = [];
  
    if (totalPages <= MAX_VISIBLE_PAGES) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);
  
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      pageNumbers.push(totalPages);
    }
  
    return pageNumbers.map((page, index) => (
      page === '...' ? (
        <span key={index} className="pagination-ellipsis">...</span>
      ) : (
        <button
          key={index}
          className={`pagination-button ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      )
    ));
  };

  return (
    <div className="results-section">
      {showEmptyState ? (
        <div className="empty-state-wrapper">
          <EmptyState handleAddRow={handleAddRow} setCurrentView={setCurrentView} />
        </div>
      ) : null}
      
      <div className="table-container">
        <table className="results-table">
          {renderTableHeader()}
          <tbody>
            {!showEmptyState && (
              <>
                {isAddingNewRow && renderInputRow()}
                {currentRows.map(renderEmployeeRow)}
              </>
            )}
          </tbody>
        </table>
      </div>
      
      <table className="fake-row-table">
        <tbody>
          <tr className="fake-row">
            <td colSpan="16">
              <button className="import-data-button" onClick={() => setCurrentView("upload")}>
                <img src={importDataIcon} alt="Import Data" className="icon" />
                Import data
              </button>
              <button className="new-employee-button" onClick={handleAddRow}>
                <img src={plusIcon} alt="New Employee" className="icon" />
                New employee
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      
      <div className="footer-section">
        <div className="total-members">
          Team Members: {rows.length}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-arrow"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <img src={paginationLeftIcon} alt="Previous" />
          </button>
          {renderPageNumbers()}
          <button
            className="pagination-arrow"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <img src={paginationRightIcon} alt="Next" />
          </button>
        </div>
      </div>
      
      {tooltip.show && (
        <DataSourceDisplay 
          source={tooltip.source} 
          position={tooltip.position}
        />
      )}
      
      {hoursWorkedTooltip.show && (
        <HoursWorkedTooltip 
          position={hoursWorkedTooltip.position}
        />
      )}
      {priorYearTooltip.show && (
        <PriorYearPayrollValueTooltip position={priorYearTooltip.position} />
      )}
    </div>
  );
};

export default ResultsTable;